import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import ConfidenceBadge from "./ConfidenceBadge";

// Current -> Potential Growth Score with the route of weighted opportunities.
export default function ForecastPanel({ forecast, full = false }) {
  const navigate = useNavigate();
  if (!forecast) return null;
  const { current, potential, gain, route = [] } = forecast;
  const items = full ? route : route.slice(0, 5);
  const clamp = (v) => `${Math.max(0, Math.min(100, v))}%`;

  return (
    <div className="card p-6 sm:p-7 h-full flex flex-col" data-testid="forecast-panel">
      <div className="flex items-center gap-2">
        <span className="grid place-items-center w-7 h-7 rounded-lg" style={{ background: "var(--grad-green-soft)" }}>
          <TrendingUp size={15} strokeWidth={2} color="#5F8213" />
        </span>
        <span className="label-caps">Your Growth Forecast</span>
      </div>

      <div className="flex items-end gap-3 mt-4">
        <div>
          <div className="text-4xl font-extrabold tracking-tighter text-ink leading-none" data-testid="forecast-current">{current}</div>
          <div className="label-caps mt-1.5">Current</div>
        </div>
        <ArrowRight size={22} strokeWidth={2.5} className="mb-3.5" style={{ color: "#B9BAB3" }} />
        <div>
          <div className="text-4xl font-extrabold tracking-tighter leading-none" style={{ color: "#5F8213" }} data-testid="forecast-potential">{potential}</div>
          <div className="label-caps mt-1.5">Potential</div>
        </div>
        <div className="ml-auto text-right">
          <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-extrabold" style={{ background: "rgba(147,192,32,.14)", color: "#5F8213" }} data-testid="forecast-gain">
            +{gain}
          </div>
          <div className="label-caps mt-1.5">Potential gain</div>
        </div>
      </div>

      {/* Progress track: current (ink) + achievable uplift (green) */}
      <div className="mt-5 relative h-2.5 rounded-full" style={{ background: "#EDEEE9" }}>
        <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: clamp(current), background: "var(--grad-ink)" }} />
        <motion.div initial={{ width: 0 }} animate={{ width: clamp(potential - current) }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-y-0 rounded-full" style={{ left: clamp(current), background: "var(--grad-green)" }} />
      </div>

      <p className="text-xs text-ink-3 mt-2">Your route from <span className="font-bold text-ink">{current}</span> → <span className="font-bold" style={{ color: "#5F8213" }}>{potential}</span></p>

      <div className="mt-4 space-y-1.5 flex-1">
        {items.map((o) => (
          <div key={o.id} className="flex items-start gap-3 rounded-xl p-2.5 transition-colors hover:bg-[#F7F8F6]" data-testid={`forecast-route-${o.id}`}>
            <span className="grid place-items-center min-w-[34px] h-7 rounded-md text-xs font-extrabold px-1.5" style={{ background: "rgba(147,192,32,.14)", color: "#5F8213" }}>+{o.uplift}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-ink leading-snug">{o.title}</span>
                <ConfidenceBadge level={o.confidence} />
              </div>
              {full && <p className="text-sm text-ink-2 mt-1 leading-relaxed">{o.body || o.evidence}</p>}
            </div>
            {full && (
              <button className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all text-ink shrink-0" onClick={() => navigate(`/app${o.action.route}`)} data-testid={`forecast-action-${o.id}`}>
                {o.action.label} <ArrowRight size={14} strokeWidth={2} />
              </button>
            )}
          </div>
        ))}
      </div>

      {!full && (
        <button className="btn-secondary focus-ring mt-4 w-full justify-center" onClick={() => navigate("/app/forecast")} data-testid="see-full-forecast">
          See your full forecast <ArrowRight size={15} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}
